import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { PlusIcon } from '@heroicons/react/24/outline';
import ClientReturnDebitDocuments from './ClientReturnDebitDocuments';
import ClientReturnDebitNotes from './ClientReturnDebitNotes';
import ClientReturnDebitEmailTab from './ClientReturnDebitEmailTab';
import ClientReturnDebitWhatsAppTab from './ClientReturnDebitWhatsAppTab';

interface Client {
  id: number;
  name: string;
  surname: string;
  email: string;
  mobile_number?: string;
}

interface Policy {
  id: number;
  policy_number: string;
  product_type: string;
  status: string;
}

interface ReturnDebit {
  id: number | string;
  created_date: string;
  reference_number: string;
  client_name: string;
  consultant_name: string;
  consultant_id?: number;
  return_debit_method: string;
  description: string;
  status: string;
  policy_number?: string;
  policy_id?: number;
  date_received?: string;
  date_sign_off?: string;
  signed_off_by?: string;
  resolution_method?: string;
  date_received_forced?: string;
  date_forced_debit?: string;
}

interface ReturnDebitDetail extends ReturnDebit {
  date_received: string;
  date_sign_off: string;
  policy_id?: number;
  client_id: number;
  originalDescription?: string;
  resolution_method?: string;
  date_received_forced?: string;
  date_forced_debit?: string;
  consultant_id?: number;
}

interface ClientReturnDebitsTabProps {
  clientId: string;
}

const API_URL = process.env.REACT_APP_API_URL || '';

const ClientReturnDebitsTab: React.FC<ClientReturnDebitsTabProps> = ({ clientId }) => {
  const location = useLocation();
  // View state
  const [view, setView] = useState<'list' | 'detail'>('list');
  const [selectedReturnDebitId, setSelectedReturnDebitId] = useState<string | null>(null);
  const [selectedReturnDebit, setSelectedReturnDebit] = useState<ReturnDebitDetail | null>(null);
  const [activeTab, setActiveTab] = useState<'details' | 'documents' | 'notes' | 'whatsapp' | 'email'>('details');
  const [isSaving, setIsSaving] = useState(false);
  const [signedOffByName, setSignedOffByName] = useState<string>('');
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Return debits list state
  const [returnDebits, setReturnDebits] = useState<ReturnDebit[]>([]);
  const [isLoadingReturnDebits, setIsLoadingReturnDebits] = useState(false);

  // Form state (for modal)
  const [formData, setFormData] = useState({
    policyId: '',
    returnDebitMethod: '',
    description: '',
    assignedTo: ''
  });

  // Data state
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [employees, setEmployees] = useState<ReturnDebitConsultant[]>([]);
  const [isLoadingPolicies, setIsLoadingPolicies] = useState(false);
  const [isLoadingemployees, setIsLoadingemployees] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Static options
  const returnDebitMethods = [
    { value: 'face_to_face', label: 'Face To Face' },
    { value: 'email', label: 'E-Mail' },
    { value: 'phone_call', label: 'Phone Call' },
    { value: 'whatsapp', label: 'Whatsapp' }
  ];

  const returnDebitStatuses = [
    { value: 'registered', label: 'Registered' },
    { value: 'send_to_client', label: 'Send To Client' },
    { value: 'sent_to_insurer', label: 'Sent To Insurer' },
    { value: 'schedule_sent_to_client', label: 'Schedule Sent To Client' },
    { value: 'completed', label: 'Completed' }
  ];

  // Fetch initial data
useEffect(() => {
  fetchClientDetails();
  fetchReturnDebits();
  fetchPolicies(clientId);
  fetchEmployees(); // ADD THIS to fetch consultants when component loads
}, [clientId]);

  // ADD THIS - Check if we need to open a specific return debit
useEffect(() => {
  const state = location.state as { returnDebitId?: string; activeTab?: string };
  console.log('🔍 ClientReturnDebitsTab - location state:', state);
  console.log('🔍 ClientReturnDebitsTab - initialLoadComplete:', initialLoadComplete);
  console.log('🔍 ClientReturnDebitsTab - returnDebits.length:', returnDebits.length);
  
  // Check that returnDebitId exists and is a string
  if (state?.returnDebitId && initialLoadComplete && returnDebits.length > 0) {
    const returnDebitId = state.returnDebitId;
    console.log('🔍 Opening return debit from navigation:', returnDebitId);
    setTimeout(() => {
      handleReferenceClick(returnDebitId);
    }, 200);
    window.history.replaceState({}, document.title);
  }
}, [location.state, initialLoadComplete, returnDebits.length]);

  const fetchReturnDebits = async () => {
    setIsLoadingReturnDebits(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const response = await axios.get(
        `${API_URL}/api/return-debits?clientId=${clientId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('📦 API Response:', response.data);
      
      let returnDebitsData = [];
      if (response.data && Array.isArray(response.data)) {
        returnDebitsData = response.data;
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        returnDebitsData = response.data.data;
      }
      
      console.log('📊 Return debits data:', returnDebitsData);
      
      const mappedReturnDebits = returnDebitsData.map((item: any) => ({
  id: item.id,
  created_date: item.created_at,
  reference_number: item.reference_number || `RD-${item.id}`,
    consultant_name: item.consultant_name && item.consultant_surname 
    ? `${item.consultant_name} ${item.consultant_surname}`
    : 'Unassigned',
  consultant_id: item.consultant_id,
  policy_id: item.policy_id,
  policy_number: item.policy_number || 'N/A',
  return_debit_method: item.return_debit_method,
  description: item.description,
  status: item.status,
  date_received: item.date_received,
  date_sign_off: item.date_sign_off,
  signed_off_by: item.signed_off_by,
  resolution_method: item.resolution_method,
}));
      
      console.log('✅ Mapped return debits:', mappedReturnDebits);
      setReturnDebits(mappedReturnDebits);
      setInitialLoadComplete(true);
    } catch (error) {
      console.error('Error fetching return debits:', error);
      setInitialLoadComplete(true);
    } finally {
      setIsLoadingReturnDebits(false);
    }
  };

  const fetchReturnDebitDetail = async (returnDebitId: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const response = await axios.get(
        `${API_URL}/api/return-debits/${returnDebitId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const data = response.data.data || response.data;
            setSelectedReturnDebit({
  ...data,
  signed_off_by: data.signed_off_by || '',
  consultant_id: data.consultant_id
});
      setSignedOffByName(data.signed_off_by || '');
    } catch (error) {
      console.error('Error fetching return debit details:', error);
      setError('Failed to load return debit details');
    }
  };

  const fetchPolicies = async (clientId: string) => {
    setIsLoadingPolicies(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const response = await axios.get(
        `${API_URL}/api/return-debits/policies?clientId=${clientId}&status=active`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('📋 Policies API response:', response.data);
      
      let policiesData = [];
      if (Array.isArray(response.data)) {
        policiesData = response.data;
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        policiesData = response.data.data;
      }
      
      setPolicies(policiesData);
      console.log('✅ Policies loaded:', policiesData.length);
    } catch (error) {
      console.error('Error fetching policies:', error);
      setPolicies([]);
    } finally {
      setIsLoadingPolicies(false);
    }
  };

  const fetchClientDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const response = await axios.get(
  `${API_URL}/api/clients/${clientId}`,
  { headers: { Authorization: `Bearer ${token}` } }
);
      
      const client = response.data;
      setSelectedClient(client);
    } catch (error) {
      console.error('Error fetching client details:', error);
    }
  };

  const fetchEmployees = async () => {
  setIsLoadingemployees(true);
  setError('');
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('❌ No authentication token found');
      throw new Error('No authentication token found');
    }

    console.log('🔍 Fetching consultants from:', `${API_URL}/api/consultants`);
    
    const response = await axios.get(
      `${API_URL}/api/consultants`,
      { 
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        } 
      }
    );
    
    console.log('📥 Full API response:', response);
    console.log('📥 Response data:', response.data);
    
    let consultantsData: any[] = [];
    
    if (response.data && response.data.success && Array.isArray(response.data.data)) {
      consultantsData = response.data.data;
      console.log('✅ Found consultants in response.data.data:', consultantsData.length);
    } else if (Array.isArray(response.data)) {
      consultantsData = response.data;
      console.log('✅ Found consultants in response.data (array):', consultantsData.length);
    } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
      consultantsData = response.data.data;
      console.log('✅ Found consultants in response.data.data (nested):', consultantsData.length);
    } else {
      console.warn('⚠️ Unexpected response format:', response.data);
      consultantsData = [];
    }
    
    console.log('📋 Raw consultants data:', consultantsData);
    
    const activeConsultants = consultantsData
      .filter((c: any) => c.active !== false)
      .map((c: any) => ({
        id: Number(c.id),
        name: c.name || '',
        surname: c.surname || '',
        email: c.email || '',
        role: c.role || 'consultant'
      }));
    
    console.log('✅ Active consultants loaded:', activeConsultants.length);
    console.log('📋 Active consultants:', activeConsultants);
    
    setEmployees(activeConsultants);
    
  } catch (error: any) {
    console.error('❌ Error fetching consultants:', error);
    console.error('Error details:', error.response?.data);
    setEmployees([]);
    setError('Failed to load consultants: ' + (error.response?.data?.message || error.message));
  } finally {
    setIsLoadingemployees(false);
  }
};

  const handleOpenModal = () => {
    setFormData({
      policyId: '',
      returnDebitMethod: '',
      description: '',
      assignedTo: ''
    });
    setError('');
    fetchEmployees();
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setError('');
  };

  const handleReferenceClick = async (returnDebitId: string) => {
  setSelectedReturnDebitId(returnDebitId);
  
  // Fetch consultants FIRST
  console.log('🔍 Fetching consultants before showing detail...');
  await fetchEmployees();
  
  const returnDebit = returnDebits.find(r => r.id.toString() === returnDebitId);
  if (returnDebit) {
    console.log('🔍 Selected Return Debit:', returnDebit);
    console.log('🔍 Policy ID:', returnDebit.policy_id);
    console.log('🔍 Policy Number:', returnDebit.policy_number);
    
    setSelectedReturnDebit({
      ...returnDebit,
      date_received: returnDebit.date_received || '',
      date_sign_off: returnDebit.date_sign_off || '',
      client_id: parseInt(clientId),
      originalDescription: returnDebit.description,
      resolution_method: returnDebit.resolution_method || '',
      policy_id: returnDebit.policy_id,
      policy_number: returnDebit.policy_number,
            consultant_id: returnDebit.consultant_id
    } as ReturnDebitDetail);
  }
  setView('detail');
  fetchPolicies(clientId);
};

  const handleBackToList = () => {
    setView('list');
    setSelectedReturnDebitId(null);
    setSelectedReturnDebit(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.returnDebitMethod || !formData.description) {
      setError('Please fill in all required fields');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const payload = {
        client_id: parseInt(clientId),
        policy_id: formData.policyId ? parseInt(formData.policyId) : null,
        return_debit_method: formData.returnDebitMethod,
        description: formData.description,
                consultant_id: formData.assignedTo ? parseInt(formData.assignedTo) : null,
        status: 'pending',
        date_received: new Date().toISOString().split('T')[0]
      };

      await axios.post(
        `${API_URL}/api/return-debits`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess('Return debit created successfully!');
      handleCloseModal();
      setFormData({
        policyId: '',
        returnDebitMethod: '',
        description: '',
        assignedTo: ''
      });
      await fetchReturnDebits();
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (error: any) {
      console.error('Error creating return debit:', error);
      setError(error.response?.data?.message || 'Failed to create return debit');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!selectedReturnDebit || selectedReturnDebit.status === newStatus) return;
    
    setIsSaving(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const response = await axios.put(
        `${API_URL}/api/return-debits/${selectedReturnDebit.id}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.message === 'Return debit updated successfully' || response.status === 200) {
        setSelectedReturnDebit({
          ...selectedReturnDebit,
          status: newStatus
        });
        setSuccess('Return debit status updated successfully');
        setTimeout(() => setSuccess(''), 3000);
        fetchReturnDebits();
      }
    } catch (error: any) {
      console.error('Error updating status:', error);
      setError(error.response?.data?.message || 'Failed to update return debit status');
      setTimeout(() => setError(''), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSignedOff = async () => {
  if (!selectedReturnDebit) return;
  
  setIsSaving(true);
  setError('');
  
  try {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('No authentication token found');
    
    const today = new Date().toISOString().split('T')[0];
    
    const storedUser = localStorage.getItem('user');
    let signedOffBy = '';
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        const name = user.username || user.email || 'System';
        const surname = user.surname || '';
        signedOffBy = `${name} ${surname}`.trim() || name || 'System';
      } catch (e) {
        signedOffBy = 'System';
      }
    }
    
    const response = await axios.put(
      `${API_URL}/api/return-debits/${selectedReturnDebit.id}`,
      { 
        date_sign_off: today,
        status: 'completed',
        signed_off_by: signedOffBy
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    if (response.data.message === 'Return debit updated successfully' || response.status === 200) {
      setSuccess('Return debit signed off successfully');
      
      // Navigate back to list view after successful sign-off
      setView('list');
      setSelectedReturnDebitId(null);
      setSelectedReturnDebit(null);
      
      // Refresh the return debits list
      await fetchReturnDebits();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    }
  } catch (error: any) {
    console.error('Error signing off return debit:', error);
    setError(error.response?.data?.message || 'Failed to sign off return debit');
    setTimeout(() => setError(''), 3000);
  } finally {
    setIsSaving(false);
  }
};

  const formatDate = (dateString: string): string => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatLabel = (value: string): string => {
    if (!value) return 'Open';
    return value.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  // Return Debits Section Component
  const ReturnDebitSection: React.FC<{
    title: string;
    returnDebits: ReturnDebit[];
    onReferenceClick: (id: string) => void;
    formatDate: (date: string) => string;
    formatLabel: (value: string) => string;
  }> = ({ title, returnDebits, onReferenceClick, formatDate, formatLabel }) => {
    if (returnDebits.length === 0) {
      return (
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-3 text-gray-800">{title} ({returnDebits.length})</h3>
          <p className="text-gray-500 italic">No {title.toLowerCase()}</p>
        </div>
      );
    }

    return (
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-3 text-gray-800">{title} ({returnDebits.length})</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200 rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-600 border-b">Return Debit No</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-600 border-b">Description</th>
                                <th className="px-4 py-2 text-left text-sm font-medium text-gray-600 border-b">Consultant</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-600 border-b">Created</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-600 border-b">Last Updated</th>
                <th className="px-4 py-2 text-center text-sm font-medium text-gray-600 border-b">Status</th>
              </tr>
            </thead>
            <tbody>
              {returnDebits.map((returnDebit) => (
                <tr key={returnDebit.id} className="hover:bg-gray-50 border-b border-gray-100">
                  <td className="px-4 py-2 text-sm">
                    <button
                      onClick={() => onReferenceClick(returnDebit.id.toString())}
                      className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                    >
                      {returnDebit.reference_number}
                    </button>
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-800 max-w-xs truncate">
                    {returnDebit.description}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-600">
                    {returnDebit.consultant_name}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-600">
                    {formatDate(returnDebit.created_date)}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-600">
                    {formatDate(returnDebit.date_sign_off || returnDebit.created_date)}
                  </td>
                  <td className="px-4 py-2 text-sm text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        returnDebit.status?.toLowerCase() === 'completed'
                        ? 'bg-green-100 text-green-800'
                        : returnDebit.status?.toLowerCase() === 'cancelled'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                        {returnDebitStatuses.find(s => s.value === returnDebit.status)?.label || formatLabel(returnDebit.status || 'Open')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const handleReturnDebitMethodChange = async (newMethod: string) => {
    if (!selectedReturnDebit || selectedReturnDebit.return_debit_method === newMethod) return;
    
    setIsSaving(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const response = await axios.put(
        `${API_URL}/api/return-debits/${selectedReturnDebit.id}`,
        { return_debit_method: newMethod },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.message === 'Return debit updated successfully' || response.status === 200) {
        setSelectedReturnDebit({
          ...selectedReturnDebit,
          return_debit_method: newMethod
        });
        setSuccess('Method updated successfully');
        setTimeout(() => setSuccess(''), 3000);
        fetchReturnDebits();
      }
    } catch (error: any) {
      console.error('Error updating method:', error);
      setError(error.response?.data?.message || 'Failed to update method');
      setTimeout(() => setError(''), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDescriptionUpdate = async () => {
    if (!selectedReturnDebit) return;
    
    setIsSaving(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const response = await axios.put(
        `${API_URL}/api/return-debits/${selectedReturnDebit.id}`,
        { description: selectedReturnDebit.description },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.message === 'Return debit updated successfully' || response.status === 200) {
        setSuccess('Description updated successfully');
        setTimeout(() => setSuccess(''), 3000);
        fetchReturnDebits();
        setSelectedReturnDebit({
          ...selectedReturnDebit,
          originalDescription: selectedReturnDebit.description
        });
      }
    } catch (error: any) {
      console.error('Error updating description:', error);
      setError(error.response?.data?.message || 'Failed to update description');
      setTimeout(() => setError(''), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePolicyChange = async (newPolicyId: string) => {
    if (!selectedReturnDebit) return;
    
    setIsSaving(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const policyId = newPolicyId ? parseInt(newPolicyId) : null;
      const selectedPolicy = policies.find(p => p.id === policyId);
      
      const response = await axios.put(
        `${API_URL}/api/return-debits/${selectedReturnDebit.id}`,
        { policy_id: policyId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.message === 'Return debit updated successfully' || response.status === 200) {
        setSelectedReturnDebit({
          ...selectedReturnDebit,
          policy_id: policyId || undefined,
          policy_number: selectedPolicy?.policy_number || 'No policy selected'
        });
        setSuccess('Policy updated successfully');
        setTimeout(() => setSuccess(''), 3000);
        fetchReturnDebits();
      }
    } catch (error: any) {
      console.error('Error updating policy:', error);
      setError(error.response?.data?.message || 'Failed to update policy');
      setTimeout(() => setError(''), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDateReceivedChange = async (newDate: string) => {
    if (!selectedReturnDebit) return;
    
    setIsSaving(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const response = await axios.put(
        `${API_URL}/api/return-debits/${selectedReturnDebit.id}`,
        { date_received: newDate },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.message === 'Return debit updated successfully' || response.status === 200) {
        setSelectedReturnDebit({
          ...selectedReturnDebit,
          date_received: newDate
        });
        setSuccess('Date received updated successfully');
        setTimeout(() => setSuccess(''), 3000);
        fetchReturnDebits();
      }
    } catch (error: any) {
      console.error('Error updating date received:', error);
      setError(error.response?.data?.message || 'Failed to update date received');
      setTimeout(() => setError(''), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  // Resolution Method Change Handler
const handleResolutionMethodChange = async (newMethod: string) => {
  if (!selectedReturnDebit || selectedReturnDebit.resolution_method === newMethod) return;
  
  setIsSaving(true);
  setError('');
  
  try {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('No authentication token found');
    
    const response = await axios.put(
      `${API_URL}/api/return-debits/${selectedReturnDebit.id}`,
      { resolution_method: newMethod },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    if (response.data.message === 'Return debit updated successfully' || response.status === 200) {
      setSelectedReturnDebit({
        ...selectedReturnDebit,
        resolution_method: newMethod
      });
      setSuccess('Resolution method updated successfully');
      setTimeout(() => setSuccess(''), 3000);
      fetchReturnDebits();
    }
  } catch (error: any) {
    console.error('Error updating resolution method:', error);
    setError(error.response?.data?.message || 'Failed to update resolution method');
    setTimeout(() => setError(''), 3000);
  } finally {
    setIsSaving(false);
  }
};

// Date Received Forced Change Handler
const handleDateReceivedForcedChange = async (newDate: string) => {
  if (!selectedReturnDebit) return;
  
  setIsSaving(true);
  setError('');
  
  try {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('No authentication token found');
    
    const response = await axios.put(
      `${API_URL}/api/return-debits/${selectedReturnDebit.id}`,
      { date_received_forced: newDate },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    if (response.data.message === 'Return debit updated successfully' || response.status === 200) {
      setSelectedReturnDebit({
        ...selectedReturnDebit,
        date_received_forced: newDate
      });
      setSuccess('Date received updated successfully');
      setTimeout(() => setSuccess(''), 3000);
      fetchReturnDebits();
    }
  } catch (error: any) {
    console.error('Error updating date received forced:', error);
    setError(error.response?.data?.message || 'Failed to update date received');
    setTimeout(() => setError(''), 3000);
  } finally {
    setIsSaving(false);
  }
};

const handleConsultantAssignment = async (consultantId: string) => {
  if (!selectedReturnDebit) return;
  
  setIsSaving(true);
  setError('');
  
  try {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('No authentication token found');
    
    const assignedToId = consultantId ? parseInt(consultantId) : null;
    
    const response = await axios.put(
      `${API_URL}/api/return-debits/${selectedReturnDebit.id}`,
            { consultant_id: assignedToId },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    if (response.data.message === 'Return debit updated successfully' || response.status === 200) {
      const consultant = employees.find(c => c.id === assignedToId);
      const consultantName = consultant ? `${consultant.name} ${consultant.surname}` : 'Unassigned';
      
            setSelectedReturnDebit({
        ...selectedReturnDebit,
        consultant_id: assignedToId || undefined,
        consultant_name: consultantName
      });
      setSuccess('Consultant assigned successfully');
      setTimeout(() => setSuccess(''), 3000);
      fetchReturnDebits();
    }
  } catch (error: any) {
    console.error('Error assigning consultant:', error);
    setError(error.response?.data?.message || 'Failed to assign consultant');
    setTimeout(() => setError(''), 3000);
  } finally {
    setIsSaving(false);
  }
};

// Date of Forced Debit Change Handler
const handleDateForcedDebitChange = async (newDate: string) => {
  if (!selectedReturnDebit) return;
  
  setIsSaving(true);
  setError('');
  
  try {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('No authentication token found');
    
    const response = await axios.put(
      `${API_URL}/api/return-debits/${selectedReturnDebit.id}`,
      { date_forced_debit: newDate },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    if (response.data.message === 'Return debit updated successfully' || response.status === 200) {
      setSelectedReturnDebit({
        ...selectedReturnDebit,
        date_forced_debit: newDate
      });
      setSuccess('Date of forced debit updated successfully');
      setTimeout(() => setSuccess(''), 3000);
      fetchReturnDebits();
    }
  } catch (error: any) {
    console.error('Error updating date of forced debit:', error);
    setError(error.response?.data?.message || 'Failed to update date of forced debit');
    setTimeout(() => setError(''), 3000);
  } finally {
    setIsSaving(false);
  }
};

  // Detail View
  if (view === 'detail' && selectedReturnDebit) {
    const isSignedOff = !!selectedReturnDebit.date_sign_off;
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Return Debit Details</h3>
          <div className="flex gap-2">
            {!isSignedOff && selectedReturnDebit.status?.toLowerCase() === 'completed' && (
              <button
                onClick={handleSignedOff}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                Signed Off
              </button>
            )}
            <button
              onClick={handleBackToList}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Back to List
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('details')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'details'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Return Debit Details
            </button>
            <button
              onClick={() => setActiveTab('documents')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'documents'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Documents
            </button>
            <button
              onClick={() => setActiveTab('notes')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'notes'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Notes
            </button>
            <button
              onClick={() => setActiveTab('whatsapp')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'whatsapp'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              WhatsApp
            </button>
            <button
              onClick={() => setActiveTab('email')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'email'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Email
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="mt-4">
          {activeTab === 'details' && (
            <div className="bg-white shadow-md rounded-lg p-6 border">
              <div className="grid grid-cols-2 gap-6">
                {/* Reference Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reference Number</label>
                  <div className="text-sm text-gray-900 bg-gray-50 px-3 py-2 border border-gray-200 rounded-md">
                    {selectedReturnDebit.reference_number}
                  </div>
                </div>

                {/* Method Received - Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Method Received</label>
                  <select
                    value={selectedReturnDebit.return_debit_method}
                    onChange={(e) => handleReturnDebitMethodChange(e.target.value)}
                    disabled={isSaving || isSignedOff}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
  isSignedOff 
    ? 'bg-gray-100 border-gray-300 cursor-not-allowed' 
    : 'border-gray-300 bg-white'
}`}
                  >
                    <option value="face_to_face">Face To Face</option>
                    <option value="email">E-Mail</option>
                    <option value="phone_call">Phone Call</option>
                    <option value="whatsapp">WhatsApp</option>
                  </select>
                  {isSaving && (
                    <p className="text-xs text-blue-600 mt-1">Saving...</p>
                  )}
                </div>

                {/* Method Received - Dropdown */}
<div>
  <label className="block text-sm font-medium text-gray-700 mb-1">Method Received</label>
  <select
    value={selectedReturnDebit.return_debit_method}
    onChange={(e) => handleReturnDebitMethodChange(e.target.value)}
    disabled={isSaving || isSignedOff}
    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
      isSignedOff 
        ? 'bg-gray-100 border-gray-300 cursor-not-allowed' 
        : 'border-gray-300 bg-white'
    }`}
  >
    <option value="face_to_face">Face To Face</option>
    <option value="email">E-Mail</option>
    <option value="phone_call">Phone Call</option>
    <option value="whatsapp">WhatsApp</option>
  </select>
  {isSaving && (
    <p className="text-xs text-blue-600 mt-1">Saving...</p>
  )}
</div>

{/* Assign to Consultant - NEW FIELD */}
<div>
  <label className="block text-sm font-medium text-gray-700 mb-1">Assign to Consultant</label>
  <select
        value={selectedReturnDebit.consultant_id?.toString() || ''}
    onChange={(e) => handleConsultantAssignment(e.target.value)}
    disabled={isSaving || isLoadingemployees}
    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
  >
    <option value="">Unassigned</option>
    {employees.map((consultant) => (
      <option key={consultant.id} value={consultant.id.toString()}>
        {consultant.name} {consultant.surname}
      </option>
    ))}
  </select>
  {isLoadingemployees && (
    <p className="text-xs text-blue-600 mt-1">Loading consultants...</p>
  )}
  {isSaving && (
    <p className="text-xs text-blue-600 mt-1">Saving...</p>
  )}
</div>

                {/* Description - Editable */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <div className="flex gap-2">
                    <textarea
                      value={selectedReturnDebit.description}
                      onChange={(e) => {
                        setSelectedReturnDebit({
                          ...selectedReturnDebit,
                          description: e.target.value
                        });
                      }}
                      disabled={isSaving || isSignedOff}
                      rows={3}
                      className={`flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        isSignedOff 
                          ? 'bg-gray-100 cursor-not-allowed opacity-75' 
                          : 'bg-white'
                      }`}
                      placeholder="Enter description..."
                    />
                    {selectedReturnDebit.description !== selectedReturnDebit.originalDescription && !isSignedOff && (
                      <button
                        onClick={handleDescriptionUpdate}
                        disabled={isSaving}
                        className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Save
                      </button>
                    )}
                  </div>
                  {isSaving && (
                    <p className="text-xs text-blue-600 mt-1">Saving...</p>
                  )}
                </div>

                {/* Policy Number - Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Policy Number</label>
                  <select
                    value={selectedReturnDebit.policy_id ? selectedReturnDebit.policy_id.toString() : ''}
                    onChange={(e) => handlePolicyChange(e.target.value)}
                    disabled={isSaving || isLoadingPolicies || isSignedOff}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      isSignedOff 
                        ? 'bg-gray-100 cursor-not-allowed opacity-75' 
                        : 'bg-white'
                    }`}
                  >
                    <option value="">No policy selected</option>
                    {policies.map((policy) => (
                      <option key={policy.id} value={policy.id.toString()}>
                        {policy.policy_number}
                      </option>
                    ))}
                  </select>
                  {isLoadingPolicies && (
                    <p className="text-xs text-blue-600 mt-1">Loading policies...</p>
                  )}
                  {policies.length === 0 && !isLoadingPolicies && (
                    <p className="text-xs text-gray-500 mt-1">No active policies available for this client</p>
                  )}
                </div>

                {/* Status - Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Return Debit Status</label>
                  <select
                    value={selectedReturnDebit.status?.toLowerCase() || ''}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    disabled={isSaving || isSignedOff}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      isSignedOff 
                        ? 'bg-gray-100 cursor-not-allowed opacity-75' 
                        : 'bg-white'
                    }`}
                  >
                    {returnDebitStatuses.map((status) => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                  {isSaving && (
                    <p className="text-xs text-blue-600 mt-1">Saving...</p>
                  )}
                </div>

                {/* Return Debit Date - Date Picker */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Return Debit Date</label>
                  <input
                    type="date"
                    value={selectedReturnDebit.date_received?.split('T')[0] || selectedReturnDebit.created_date?.split('T')[0] || ''}
                    onChange={(e) => handleDateReceivedChange(e.target.value)}
                    disabled={isSaving || isSignedOff}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      isSignedOff 
                        ? 'bg-gray-100 cursor-not-allowed opacity-75' 
                        : 'bg-white'
                    }`}
                  />
                  {isSaving && (
                    <p className="text-xs text-blue-600 mt-1">Saving...</p>
                  )}
                </div>

                {/* Resolution Method - Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Select Return Debit Resolution Method</label>
                  <select
                    value={selectedReturnDebit.resolution_method || ''}
                    onChange={(e) => handleResolutionMethodChange(e.target.value)}
                    disabled={isSaving || isSignedOff}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      isSignedOff 
                        ? 'bg-gray-100 cursor-not-allowed opacity-75' 
                        : 'bg-white'
                    }`}
                  >
                    <option value="">Select resolution method</option>
                    <option value="double_deduction">Double Deduction</option>
                    <option value="forced_debit_order">Forced Debit Order</option>
                  </select>
                  {isSaving && (
                    <p className="text-xs text-blue-600 mt-1">Saving...</p>
                  )}
                </div>

                {/* Conditional Fields - Only show if Forced Debit Order is selected */}
                {selectedReturnDebit.resolution_method === 'forced_debit_order' && (
                  <>
                    

                    {/* Date of Forced Debit */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Date of Forced Debit</label>
                      <input
                        type="date"
                        value={selectedReturnDebit.date_forced_debit?.split('T')[0] || ''}
                        onChange={(e) => handleDateForcedDebitChange(e.target.value)}
                        disabled={isSaving || isSignedOff}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          isSignedOff 
                            ? 'bg-gray-100 cursor-not-allowed opacity-75' 
                            : 'bg-white'
                        }`}
                      />
                      {isSaving && (
                        <p className="text-xs text-blue-600 mt-1">Saving...</p>
                      )}
                    </div>
                  </>
                )}

                {/* Date Received - Only show if Forced Debit Order is selected */}
                {selectedReturnDebit.resolution_method === 'forced_debit_order' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date Received</label>
                    <input
                      type="date"
                      value={selectedReturnDebit.date_received_forced?.split('T')[0] || ''}
                      onChange={(e) => handleDateReceivedForcedChange(e.target.value)}
                      disabled={isSaving || isSignedOff}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        isSignedOff 
                          ? 'bg-gray-100 cursor-not-allowed opacity-75' 
                          : 'bg-white'
                      }`}
                    />
                    {isSaving && (
                      <p className="text-xs text-blue-600 mt-1">Saving...</p>
                    )}
                  </div>
                )}

                {/* Date Sign Off and Signed Off By - Side by side */}
                {selectedReturnDebit.date_sign_off && (
                  <div className="col-span-2">
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Date Sign Off</label>
                        <div className="text-sm text-gray-900 bg-gray-50 px-3 py-2 border border-gray-200 rounded-md">
                          {formatDate(selectedReturnDebit.date_sign_off)}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Signed Off By</label>
                        <div className="text-sm text-gray-900 bg-gray-50 px-3 py-2 border border-gray-200 rounded-md">
                          {selectedReturnDebit.signed_off_by || signedOffByName || 'System'}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                </div>

              {/* Return Debit Questions Section */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <ReturnDebitQuestions 
                  returnDebitId={selectedReturnDebit.id.toString()} 
                  clientId={clientId}
                  clientName={selectedClient?.name + ' ' + selectedClient?.surname || 'Client'}
                />
              </div>
            </div>
          )}

          {activeTab === 'documents' && (
            <div className="bg-white shadow-md rounded-lg p-6 border">
              <ClientReturnDebitDocuments 
                returnDebitId={selectedReturnDebit.id.toString()} 
                clientId={clientId}
              />
            </div>
          )}

          {activeTab === 'notes' && (
            <div className="bg-white shadow-md rounded-lg p-6 border">
              <ClientReturnDebitNotes returnDebitId={selectedReturnDebit.id.toString()} />
            </div>
          )}

          {activeTab === 'whatsapp' && (
  <div className="bg-white shadow-md rounded-lg p-6 border">
    <ClientReturnDebitWhatsAppTab 
      clientId={clientId}
      returnDebitId={selectedReturnDebit.id.toString()}
      returnDebitRef={selectedReturnDebit.reference_number || `RD-${selectedReturnDebit.id}`}
      clientName={selectedClient ? `${selectedClient.name} ${selectedClient.surname}` : ''}
      policyNumber={selectedReturnDebit.policy_number || ''}
      user={(() => {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          try {
            return JSON.parse(storedUser);
          } catch {
            return undefined;
          }
        }
        return undefined;
      })()}
    />
  </div>
)}

          {activeTab === 'email' && (
  <div className="bg-white shadow-md rounded-lg p-6 border">
    <ClientReturnDebitEmailTab 
      clientId={clientId}
      returnDebitId={selectedReturnDebit.id.toString()}
      returnDebitRef={selectedReturnDebit.reference_number || `RD-${selectedReturnDebit.id}`}
      clientName={selectedClient ? `${selectedClient.name} ${selectedClient.surname}` : ''}
      policyNumber={selectedReturnDebit.policy_number || ''}
      user={(() => {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          try {
            return JSON.parse(storedUser);
          } catch {
            return undefined;
          }
        }
        return undefined;
      })()}
    />
  </div>
)}
        </div>
      </div>
    );
  }

  // List View
  const openReturnDebits = returnDebits.filter(r => r.status?.toLowerCase() !== 'completed' && r.status?.toLowerCase() !== 'cancelled');
  const completedReturnDebits = returnDebits.filter(r => r.status?.toLowerCase() === 'completed');

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-xl font-semibold text-gray-800">Client Return Debits</h3>
          <p className="text-sm text-gray-500 mt-1">Manage client return debits and track progress</p>
        </div>
        <button
          onClick={handleOpenModal}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
        >
          <PlusIcon className="w-4 h-4 mr-2" />
          Create Return Debit
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          {error}
          <button onClick={() => setError('')} className="float-right text-red-700 hover:text-red-900">×</button>
        </div>
      )}
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg">
          {success}
        </div>
      )}

      {isLoadingReturnDebits ? (
        <div className="text-center py-8 text-gray-500">Loading return debits...</div>
      ) : (
        <>
          <ReturnDebitSection 
            title="Open Return Debits" 
            returnDebits={openReturnDebits}
            onReferenceClick={handleReferenceClick}
            formatDate={formatDate}
            formatLabel={formatLabel}
          />
          <ReturnDebitSection 
            title="Completed Return Debits" 
            returnDebits={completedReturnDebits}
            onReferenceClick={handleReferenceClick}
            formatDate={formatDate}
            formatLabel={formatLabel}
          />
        </>
      )}

      {/* Create Return Debit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={handleCloseModal}></div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                        Create New Return Debit
                      </h3>
                      
                      {error && (
                        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
                          {error}
                        </div>
                      )}

                      <div className="space-y-4">
                        <div>
                          <label htmlFor="policy" className="block text-sm font-medium text-gray-700 mb-1">
                            Select Policy <span className="text-gray-400 text-xs">(optional)</span>
                          </label>
                          <select
                            id="policy"
                            value={formData.policyId}
                            onChange={(e) => setFormData(prev => ({ ...prev, policyId: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            disabled={isLoadingPolicies}
                          >
                            <option value="">No policy selected</option>
                            {policies.map(policy => (
                              <option key={policy.id} value={policy.id}>
                                {policy.policy_number}
                              </option>
                            ))}
                          </select>
                          {isLoadingPolicies && (
                            <p className="text-xs text-blue-600 mt-1">Loading policies...</p>
                          )}
                          {policies.length === 0 && !isLoadingPolicies && (
                            <p className="text-xs text-gray-500 mt-1">No active policies found for this client</p>
                          )}
                        </div>

                        {/* Select Return Debit Method */}
                        <div>
                          <label htmlFor="returnDebitMethod" className="block text-sm font-medium text-gray-700 mb-1">
                            Method Received *
                          </label>
                          <select
                            id="returnDebitMethod"
                            value={formData.returnDebitMethod}
                            onChange={(e) => setFormData(prev => ({ ...prev, returnDebitMethod: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            required
                          >
                            <option value="">Select method received</option>
                            {returnDebitMethods.map(method => (
                              <option key={method.value} value={method.value}>
                                {method.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                                                    <label htmlFor="assignedTo" className="block text-sm font-medium text-gray-700 mb-1">
                            Select Consultant <span className="text-gray-400 text-xs">(optional)</span>
                          </label>
                          <select
                            id="assignedTo"
                            value={formData.assignedTo}
                            onChange={(e) => setFormData(prev => ({ ...prev, assignedTo: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            disabled={isLoadingemployees}
                          >
                                                        <option value="">Select consultant</option>
                            {employees.map((consultant: any) => (
                              <option key={consultant.id} value={consultant.id}>
                                {consultant.name} {consultant.surname}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                            Description *
                          </label>
                          <textarea
                            id="description"
                            value={formData.description}
                            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                            rows={4}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Enter return debit description..."
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Creating...' : 'Submit'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Return Debit Questions Component
interface ReturnDebitQuestionsProps {
  returnDebitId: string;
  clientId: string;
  clientName: string;
}

interface Question {
  id: number;
  question: string;
  answer?: boolean | null;
  is_required: boolean;
  order: number;
  options?: string[];
}

interface Answer {
  question_id: number;
  answer: boolean;
  created_at?: string;
  answered_by?: string;
}

const ReturnDebitQuestions: React.FC<ReturnDebitQuestionsProps> = ({ returnDebitId, clientId, clientName }) => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<Record<number, boolean>>({});
  const [answerTimestamps, setAnswerTimestamps] = useState<Record<number, string>>({});
  const [answerUserNames, setAnswerUserNames] = useState<Record<number, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [loggedInUserName, setLoggedInUserName] = useState<string>('');

  // Get logged-in user name when component mounts
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        const name = user.username || user.email || 'User';
        const surname = user.surname || '';
        const fullName = `${name} ${surname}`.trim();
        setLoggedInUserName(fullName || name || 'User');
      } catch (e) {
        setLoggedInUserName('User');
      }
    }
  }, []);

  // Fetch questions and existing answers
  useEffect(() => {
    fetchQuestions();
  }, [returnDebitId]);

  const fetchQuestions = async () => {
    setIsLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || '';

      console.log('🔍 Fetching return debit definitions...');

      // Fetch from return debit definitions (process definitions)
      const questionsResponse = await fetch(`${API_URL}/api/return-debit-definitions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('📡 Response status:', questionsResponse.status);

      if (!questionsResponse.ok) {
        console.warn('⚠️ Failed to fetch return debit definitions, using fallback data');
        setQuestions([
          { 
            id: 1, 
            question: "Has the client confirmed the return debit amount?", 
            is_required: true, 
            order: 1,
            options: []
          },
          { 
            id: 2, 
            question: "Has the policy been cancelled for the return debit?", 
            is_required: false, 
            order: 2,
            options: []
          }
        ]);
        setIsLoading(false);
        return;
      }

      const questionsData = await questionsResponse.json();
      console.log('📦 Questions data received:', questionsData);
      
      let questionsList: Question[] = [];
      
      let rawQuestions: any[] = [];
      if (questionsData && questionsData.success && Array.isArray(questionsData.data)) {
        rawQuestions = questionsData.data;
      } else if (questionsData && Array.isArray(questionsData)) {
        rawQuestions = questionsData;
      } else if (questionsData && questionsData.data && Array.isArray(questionsData.data)) {
        rawQuestions = questionsData.data;
      }
      
      console.log('📋 Raw questions array:', rawQuestions);
      
      questionsList = rawQuestions
        .filter((q: any) => q.isActive !== false)
        .map((q: any) => ({
          id: q.id,
          question: q.question || q.text || '',
          is_required: q.isRequired || q.is_required || false,
          order: q.order || 0,
          options: q.options || []
        }));
      
      console.log('✅ Processed questions list:', questionsList);
      setQuestions(questionsList);

      // Fetch existing answers for this return debit
      const answersResponse = await fetch(`${API_URL}/api/return-debit-questions/answers/${returnDebitId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      let existingAnswers: Record<number, boolean> = {};
      let existingTimestamps: Record<number, string> = {};
      let existingUserNames: Record<number, string> = {};
      
      if (answersResponse.ok) {
        const answersData = await answersResponse.json();
        
        if (answersData && answersData.success && Array.isArray(answersData.data)) {
          console.log('📝 Processing answers:', answersData.data);
          answersData.data.forEach((answer: any) => {
            console.log('🔍 Processing answer:', answer);
            existingAnswers[answer.question_id] = answer.answer;
            if (answer.created_at) {
              existingTimestamps[answer.question_id] = answer.created_at;
            }
            if (answer.answered_by) {
              existingUserNames[answer.question_id] = answer.answered_by;
              console.log('👤 Set answered_by for question', answer.question_id, ':', answer.answered_by);
            }
          });
        }
      }
      
      setAnswers(existingAnswers);
      setAnswerTimestamps(existingTimestamps);
      setAnswerUserNames(existingUserNames);
      
    } catch (error) {
      console.error('❌ Error fetching questions:', error);
      setError('Failed to load questions from server');
      setQuestions([
        { 
          id: 1, 
          question: "Has the client confirmed the return debit amount?", 
          is_required: true, 
          order: 1,
          options: []
        },
        { 
          id: 2, 
          question: "Has the policy been cancelled for the return debit?", 
          is_required: false, 
          order: 2,
          options: []
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAnswerChange = (questionId: number, value: boolean) => {
    if (answerTimestamps[questionId] === undefined) {
      setAnswers(prev => ({
        ...prev,
        [questionId]: value
      }));
    }
  };

  const handleSaveAnswers = async () => {
    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || '';

      let signedOffBy = '';
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const user = JSON.parse(storedUser);
          const name = user.username || user.email || 'User';
          const surname = user.surname || '';
          signedOffBy = `${name} ${surname}`.trim() || name || 'User';
        } catch (e) {
          signedOffBy = 'User';
        }
      }

      const answersToSave = Object.entries(answers).map(([questionId, answer]) => ({
        return_debit_id: parseInt(returnDebitId),
        question_id: parseInt(questionId),
        answer: answer,
        answered_by: signedOffBy
      }));

      const response = await fetch(`${API_URL}/api/return-debit-questions/answers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ answers: answersToSave }),
      });

      if (!response.ok) {
        throw new Error('Failed to save answers');
      }

      await fetchQuestions();
      setSuccess('Answers saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      console.error('Error saving answers:', error);
      setError(error.message || 'Failed to save answers');
    } finally {
      setIsSaving(false);
    }
  };

  const formatDateTime = (dateString: string): string => {
    return new Date(dateString).toLocaleString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="bg-white shadow-md rounded-lg p-6 border">
        <div className="text-center text-gray-500">Loading questions...</div>
      </div>
    );
  }

  if (questions.length === 0 && !isLoading) {
    return (
      <div className="bg-white shadow-md rounded-lg p-6 border">
        <h4 className="text-md font-semibold text-gray-900 mb-2">Return Debit Questions</h4>
        <div className="text-center py-4">
          <p className="text-sm text-gray-500">No active return debit questions found.</p>
          <p className="text-xs text-gray-400 mt-1">Please add return debit questions in Process Definitions.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-md rounded-lg p-6 border">
      <h4 className="text-md font-semibold text-gray-900 mb-4">Return Debit Questions</h4>
      
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
          {error}
        </div>
      )}
      
      {success && (
        <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded text-sm">
          {success}
        </div>
      )}

      <div className="space-y-4">
        {questions.map((question) => {
          const isAnswered = answerTimestamps[question.id] !== undefined;
          const answeredBy = answerUserNames[question.id] || '';
          
          return (
            <div 
              key={question.id} 
              className={`p-4 rounded-lg border transition-colors ${
                isAnswered 
                  ? 'bg-gray-100 border-gray-300' 
                  : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
              }`}
            >
              <p className={`text-sm mb-3 ${isAnswered ? 'text-gray-500' : 'text-gray-900'}`}>
                {question.question}
              </p>
              
              <div className="flex space-x-6">
                <label className={`flex items-center space-x-2 ${isAnswered ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                  <input
                    type="radio"
                    name={`question-${question.id}`}
                    checked={answers[question.id] === true}
                    onChange={() => handleAnswerChange(question.id, true)}
                    disabled={isAnswered}
                    className={`w-4 h-4 ${
                      isAnswered 
                        ? 'text-gray-400 border-gray-300 cursor-not-allowed' 
                        : 'text-blue-600 border-gray-300 focus:ring-blue-500'
                    }`}
                  />
                  <span className={`text-sm ${isAnswered ? 'text-gray-400' : 'text-gray-700'}`}>
                    Yes
                  </span>
                </label>
                
                <label className={`flex items-center space-x-2 ${isAnswered ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                  <input
                    type="radio"
                    name={`question-${question.id}`}
                    checked={answers[question.id] === false}
                    onChange={() => handleAnswerChange(question.id, false)}
                    disabled={isAnswered}
                    className={`w-4 h-4 ${
                      isAnswered 
                        ? 'text-gray-400 border-gray-300 cursor-not-allowed' 
                        : 'text-blue-600 border-gray-300 focus:ring-blue-500'
                    }`}
                  />
                  <span className={`text-sm ${isAnswered ? 'text-gray-400' : 'text-gray-700'}`}>
                    No
                  </span>
                </label>
              </div>

              {/* Show stamp if question has been answered */}
              {isAnswered && answerTimestamps[question.id] && (
                <div className="mt-3 flex items-center">
                  <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-600 border border-gray-300">
                    <svg className="w-3 h-3 mr-1 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                    <span className="mr-1 font-semibold">{answeredBy || loggedInUserName || 'User'}</span>
                    <span>•</span>
                    <span className="ml-1">{formatDateTime(answerTimestamps[question.id])}</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Only show Save button if there are unsaved answers */}
      {Object.keys(answers).length > Object.keys(answerTimestamps).length && (
        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSaveAnswers}
            disabled={isSaving}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Saving...' : 'Save Answers'}
          </button>
        </div>
      )}
    </div>
  );
};

// Types
interface ReturnDebitConsultant {
  id: number;
  name: string;
  surname: string;
  email: string;
  active?: boolean;
}

export default ClientReturnDebitsTab;